/**
 * Analytics metric catalog — ONE definition per metric (formula, time anchor, source, unit and
 * which direction is good). The API computes them, the dashboard shows the formula in tooltips
 * and docs/analytics.md documents the same text: a number means the same thing everywhere.
 */
import { CANCELLED_STATUSES, DRIVER_ACTIVE_STATUSES, PENDING_STATUSES, SERVICE_STATUSES, type ServiceStatus } from '../service/state-machine';

// ------------------------------------------------------------------ status groups
/**
 * Every service status belongs to exactly one analytics group, so the groups always add up to
 * the number of services requested.
 * - cancelled: someone called the trip off (customer, driver, central) or the customer did not show.
 * - unfulfilled: the operation could not serve it (no driver accepted in time, or rejected).
 */
export const STATUS_GROUPS = {
  pending: PENDING_STATUSES,
  in_progress: DRIVER_ACTIVE_STATUSES,
  completed: ['COMPLETED', 'CLOSED'],
  cancelled: [...CANCELLED_STATUSES, 'NO_SHOW'],
  unfulfilled: ['EXPIRED', 'REJECTED'],
  disputed: ['DISPUTED'],
} as const satisfies Record<string, readonly ServiceStatus[]>;

export type StatusGroup = keyof typeof STATUS_GROUPS;
export const STATUS_GROUP_KEYS = Object.keys(STATUS_GROUPS) as StatusGroup[];

export const STATUS_GROUP_LABEL: Record<StatusGroup, string> = {
  pending: 'Esperando conductor',
  in_progress: 'En curso',
  completed: 'Completados',
  cancelled: 'Cancelados',
  unfulfilled: 'Sin atender',
  disputed: 'En disputa',
};

export function statusGroup(status: ServiceStatus): StatusGroup {
  for (const g of STATUS_GROUP_KEYS) if ((STATUS_GROUPS[g] as readonly string[]).includes(status)) return g;
  throw new Error(`status without analytics group: ${status}`);
}

// all statuses are covered (checked once at load: a new status must be classified)
for (const s of SERVICE_STATUSES) statusGroup(s);

/** SQL list literal of a status group, e.g. `('COMPLETED','CLOSED')`. Values are compile-time constants. */
export const sqlStatusList = (statuses: readonly string[]) => `(${statuses.map((s) => `'${s}'`).join(',')})`;

/** Offer outcomes the driver actually decided on (CANCELLED = withdrawn because the trip was taken or cancelled). */
export const ANSWERABLE_OFFER_STATUSES = ['ACCEPTED', 'REJECTED', 'EXPIRED'] as const;

// ------------------------------------------------------------------ catalog
export type MetricUnit = 'count' | 'ratio' | 'seconds' | 'money' | 'number';
/** Whether going up is good news (drives arrow color), bad news, or neither. */
export type MetricDirection = 'up_good' | 'down_good' | 'neutral';

export interface MetricDefinition {
  label: string;
  question: string;
  formula: string;
  /** Which timestamp places a service inside the period. */
  anchor: string;
  source: string;
  unit: MetricUnit;
  direction: MetricDirection;
}

/** Shared wording for the period anchor of service metrics. */
export const SERVICE_ANCHOR =
  'Fecha del servicio: la hora programada si el servicio es programado; si no, la hora de solicitud (coalesce(scheduled_at, created_at)), en la zona horaria del tenant.';

export const METRICS = {
  requested: {
    label: 'Servicios solicitados',
    question: '¿Cuántos servicios tenemos?',
    formula: 'COUNT(services) con fecha del servicio dentro del período.',
    anchor: SERVICE_ANCHOR,
    source: 'services',
    unit: 'count',
    direction: 'up_good',
  },
  completed: {
    label: 'Completados',
    question: '¿Cuántos se completaron?',
    formula: "COUNT(services WHERE status IN ('COMPLETED','CLOSED')).",
    anchor: SERVICE_ANCHOR,
    source: 'services.status',
    unit: 'count',
    direction: 'up_good',
  },
  completion_rate: {
    label: 'Tasa de finalización',
    question: '¿Qué parte de lo solicitado se completó?',
    formula: 'Completados / solicitados.',
    anchor: SERVICE_ANCHOR,
    source: 'services.status',
    unit: 'ratio',
    direction: 'up_good',
  },
  cancelled: {
    label: 'Cancelados',
    question: '¿Cuántos fueron cancelados?',
    formula: "COUNT(services WHERE status IN ('CANCELLED_BY_CLIENT','CANCELLED_BY_DRIVER','CANCELLED_BY_ADMIN','NO_SHOW')).",
    anchor: SERVICE_ANCHOR,
    source: 'services.status',
    unit: 'count',
    direction: 'down_good',
  },
  cancellation_rate: {
    label: 'Tasa de cancelación',
    question: '¿Qué parte de lo solicitado se canceló?',
    formula: 'Cancelados / solicitados. No incluye "sin atender" (EXPIRED, REJECTED), que se mide aparte.',
    anchor: SERVICE_ANCHOR,
    source: 'services.status',
    unit: 'ratio',
    direction: 'down_good',
  },
  unfulfilled: {
    label: 'Sin atender',
    question: '¿Cuántos servicios perdimos por falta de conductor?',
    formula: "COUNT(services WHERE status IN ('EXPIRED','REJECTED')): ningún conductor aceptó a tiempo o la central lo rechazó.",
    anchor: SERVICE_ANCHOR,
    source: 'services.status',
    unit: 'count',
    direction: 'down_good',
  },
  pending_now: {
    label: 'Esperando conductor ahora',
    question: '¿Cuántos servicios están esperando conductor?',
    formula: "COUNT(services WHERE status IN ('CREATED','COLLECTING_DATA','READY_FOR_DISPATCH','DISPATCHING')) en este momento, sin importar el período. Excluye programados cuyo despacho aún no empieza.",
    anchor: 'Instantáneo (ahora).',
    source: 'services.status, services.scheduled_at',
    unit: 'count',
    direction: 'down_good',
  },
  assign_time: {
    label: 'Tiempo de asignación',
    question: '¿Cuánto tardamos en asignar un conductor?',
    formula: 'accepted_at − requested_at por servicio; se muestran mediana (P50), promedio y P95. Solo servicios inmediatos (sin scheduled_at) que llegaron a tener conductor.',
    anchor: SERVICE_ANCHOR,
    source: 'services.requested_at, services.accepted_at',
    unit: 'seconds',
    direction: 'down_good',
  },
  pickup_time: {
    label: 'Tiempo hasta la recogida',
    question: '¿Cuánto tarda el conductor en llegar?',
    formula: 'arrived_at − accepted_at (mediana, promedio, P95) de los servicios donde el conductor marcó la llegada.',
    anchor: SERVICE_ANCHOR,
    source: 'services.accepted_at, services.arrived_at',
    unit: 'seconds',
    direction: 'down_good',
  },
  trip_time: {
    label: 'Duración del viaje',
    question: '¿Cuánto dura un viaje?',
    formula: 'completed_at − started_at (mediana, promedio, P95) de los servicios completados.',
    anchor: SERVICE_ANCHOR,
    source: 'services.started_at, services.completed_at',
    unit: 'seconds',
    direction: 'neutral',
  },
  total_time: {
    label: 'Solicitud a finalización',
    question: '¿Cuánto tarda un servicio desde que se pide hasta que termina?',
    formula: 'completed_at − requested_at (mediana, promedio, P95). Solo servicios inmediatos completados.',
    anchor: SERVICE_ANCHOR,
    source: 'services.requested_at, services.completed_at',
    unit: 'seconds',
    direction: 'down_good',
  },
  manual_share: {
    label: 'Requirieron intervención manual',
    question: '¿Cuántos servicios no pudo resolver el despacho automático?',
    formula: "Servicios con algún intento de despacho HUMAN o MANUAL_ASSIGN / solicitados.",
    anchor: SERVICE_ANCHOR,
    source: 'dispatch_attempts.kind',
    unit: 'ratio',
    direction: 'down_good',
  },
  active_drivers: {
    label: 'Conductores activos',
    question: '¿Cuántos conductores trabajaron?',
    formula: 'COUNT(DISTINCT driver_id) de los servicios del período que llegaron a tener conductor (accepted_at no nulo).',
    anchor: SERVICE_ANCHOR,
    source: 'services.driver_id, services.accepted_at',
    unit: 'count',
    direction: 'up_good',
  },
  fleet_utilization: {
    label: 'Utilización de la flota (ahora)',
    question: '¿Qué parte de la flota conectada está ocupada?',
    formula: 'Conductores ocupados / (disponibles + ocupados). Conectado = heartbeat dentro del timeout de presencia. No hay historial de presencia: solo existe el valor actual.',
    anchor: 'Instantáneo (ahora).',
    source: 'driver_presence',
    unit: 'ratio',
    direction: 'neutral',
  },
  acceptance_rate: {
    label: 'Tasa de aceptación',
    question: '¿Qué conductores aceptan las ofertas?',
    formula: 'Ofertas aceptadas / (aceptadas + rechazadas + expiradas). Las ofertas retiradas (CANCELLED: otro conductor tomó el servicio o se canceló) no cuentan, porque el conductor no alcanzó a decidir.',
    anchor: 'Hora en que se envió la oferta (offered_at).',
    source: 'driver_dispatch_offers.status',
    unit: 'ratio',
    direction: 'up_good',
  },
  response_time: {
    label: 'Tiempo de respuesta a ofertas',
    question: '¿Cuánto tarda el conductor en responder?',
    formula: 'Promedio de responded_at − offered_at en ofertas aceptadas o rechazadas.',
    anchor: 'Hora en que se envió la oferta (offered_at).',
    source: 'driver_dispatch_offers',
    unit: 'seconds',
    direction: 'down_good',
  },
  driver_cancellations: {
    label: 'Cancelaciones del conductor',
    question: '¿Qué conductores cancelan más?',
    formula: "Servicios CANCELLED_BY_DRIVER + servicios que el conductor soltó y volvieron a despacho (evento driver_dropped).",
    anchor: SERVICE_ANCHOR,
    source: 'services.status, service_events (driver_dropped)',
    unit: 'count',
    direction: 'down_good',
  },
  rating: {
    label: 'Calificación',
    question: '¿Cómo califican los clientes al conductor?',
    formula: 'Promedio de service_ratings.score (1–5) de las calificaciones del período.',
    anchor: 'Hora de la calificación (service_ratings.created_at).',
    source: 'service_ratings',
    unit: 'number',
    direction: 'up_good',
  },
  active_customers: {
    label: 'Clientes activos',
    question: '¿Cuántos clientes usaron el servicio?',
    formula: 'COUNT(DISTINCT customer_id) de los servicios del período (cualquier estado).',
    anchor: SERVICE_ANCHOR,
    source: 'services.customer_id',
    unit: 'count',
    direction: 'up_good',
  },
  new_customers: {
    label: 'Clientes nuevos',
    question: '¿Cuántos clientes nuevos tenemos?',
    formula: 'Clientes activos cuyo primer servicio (de toda la historia) cae dentro del período.',
    anchor: SERVICE_ANCHOR,
    source: 'services.customer_id',
    unit: 'count',
    direction: 'up_good',
  },
  returning_customers: {
    label: 'Clientes recurrentes',
    question: '¿Cuántos clientes vuelven?',
    formula: 'Clientes activos que ya tenían servicios antes del inicio del período.',
    anchor: SERVICE_ANCHOR,
    source: 'services.customer_id',
    unit: 'count',
    direction: 'up_good',
  },
  frequency: {
    label: 'Servicios por cliente',
    question: '¿Con qué frecuencia usan el servicio?',
    formula: 'Servicios solicitados / clientes activos.',
    anchor: SERVICE_ANCHOR,
    source: 'services',
    unit: 'number',
    direction: 'up_good',
  },
  retention: {
    label: 'Retención',
    question: '¿Cuántos clientes del período anterior volvieron?',
    formula: 'Clientes activos en el período de comparación que también están activos en el actual / activos en el período de comparación.',
    anchor: SERVICE_ANCHOR,
    source: 'services.customer_id',
    unit: 'ratio',
    direction: 'up_good',
  },
  trip_value: {
    label: 'Valor de viajes completados',
    question: '¿Cuánto valen los viajes que hicimos?',
    formula: "SUM(fare_total_minor) de los servicios completados. Es la tarifa cotizada y aceptada por el cliente: la plataforma no procesa el cobro del viaje, así que no es dinero recaudado.",
    anchor: SERVICE_ANCHOR,
    source: 'services.fare_total_minor',
    unit: 'money',
    direction: 'up_good',
  },
  avg_ticket: {
    label: 'Valor promedio por servicio',
    question: '¿Cuánto vale en promedio un servicio?',
    formula: 'Valor de viajes completados / servicios completados.',
    anchor: SERVICE_ANCHOR,
    source: 'services.fare_total_minor',
    unit: 'money',
    direction: 'neutral',
  },
  value_per_customer: {
    label: 'Valor por cliente',
    question: '¿Cuánto vale cada cliente?',
    formula: 'Valor de viajes completados / clientes con al menos un servicio completado.',
    anchor: SERVICE_ANCHOR,
    source: 'services',
    unit: 'money',
    direction: 'up_good',
  },
  value_per_driver: {
    label: 'Valor por conductor',
    question: '¿Cuánto mueve cada conductor?',
    formula: 'Valor de viajes completados / conductores con al menos un servicio completado.',
    anchor: SERVICE_ANCHOR,
    source: 'services',
    unit: 'money',
    direction: 'up_good',
  },
  quota_collected: {
    label: 'Cuotas recaudadas',
    question: '¿Cuánto pagaron los conductores?',
    formula: 'SUM(driver_payments.amount_minor) de pagos no anulados con fecha de pago dentro del período. Es el ingreso registrado del tenant.',
    anchor: 'Fecha de pago (driver_payments.paid_on).',
    source: 'driver_payments',
    unit: 'money',
    direction: 'up_good',
  },
  quota_pending: {
    label: 'Conductores con cuota pendiente',
    question: '¿Quién tiene pagos pendientes?',
    formula: "Suscripciones en estado GRACE_PERIOD u OVERDUE ahora mismo.",
    anchor: 'Instantáneo (estado actual de la suscripción).',
    source: 'driver_subscriptions.status',
    unit: 'count',
    direction: 'down_good',
  },
} satisfies Record<string, MetricDefinition>;

export type MetricKey = keyof typeof METRICS;

// ------------------------------------------------------------------ comparisons
export interface Comparison {
  current: number | null;
  previous: number | null;
  /** current − previous (in the metric unit; percentage points for ratios). */
  change: number | null;
  /** Relative change (0.12 = +12 %); null for ratios or when it would be meaningless. */
  pct: number | null;
  /** False when the comparison base is too small to read anything into the change. */
  reliable: boolean;
  /** Why there is no (reliable) relative change. */
  note: 'no_comparison' | 'no_data' | 'zero_base' | 'small_sample' | null;
}

/** Smallest number of events behind a comparison value for its change to be shown as reliable. */
export const MIN_COMPARISON_SAMPLE = 10;

/**
 * Compares a metric between the current and the comparison period. `samples` are the number of
 * events behind each value (services, drivers...) when the value itself is not a count.
 * A relative change is only produced when the base is non zero; it is flagged unreliable when
 * either period has fewer than MIN_COMPARISON_SAMPLE events.
 */
export function compareMetric(
  unit: MetricUnit,
  current: number | null,
  previous: number | null | undefined,
  samples?: { current: number; previous: number },
): Comparison {
  if (previous === undefined) return { current, previous: null, change: null, pct: null, reliable: false, note: 'no_comparison' };
  if (current === null || previous === null) return { current, previous, change: null, pct: null, reliable: false, note: 'no_data' };
  const change = current - previous;
  const n = samples ?? (unit === 'count' ? { current, previous } : null);
  const small = n ? Math.min(n.current, n.previous) < MIN_COMPARISON_SAMPLE : false;
  if (unit === 'ratio') return { current, previous, change, pct: null, reliable: !small, note: small ? 'small_sample' : null };
  if (previous === 0) return { current, previous, change, pct: null, reliable: false, note: 'zero_base' };
  return { current, previous, change, pct: change / Math.abs(previous), reliable: !small, note: small ? 'small_sample' : null };
}

/** a / b, or null when there is nothing to divide by. */
export const ratio = (a: number, b: number): number | null => (b > 0 ? a / b : null);
